import { Calendar as CalendarIcon, Clock, MapPin, Camera, Pen, Loader2, LucideIcon, Lightbulb } from 'lucide-react';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Dialog, DialogContent } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { RescheduleModal } from './RescheduleModal';
import { ShootingTopicsModal } from './ShootingTopicsModal';
import { cancelBookingById } from '@/services/integrations/cal';
import { toast } from 'sonner';
import { calculateCancellationFees, getCancellationConfirmationMessage, CancellationFees } from '@/utils/cancellationRules';
import { createCancellationAlert } from '@/services/business/cancellationAlerts';
import { useAuthStore } from '@/stores/authStore';
import { useBookingStore } from '@/stores/bookingStore';
import { useDirectorStore } from '@/stores/directorStore';
import { logger } from '@/utils/logger';
import { usePreloadAvatars } from '@/hooks/usePreloadAvatars';
import { User } from '@supabase/supabase-js';
type EventType = 'shooting' | 'preparation' | 'editorial';

/**
 * Creates a cancellation alert if fees apply (shooting events only)
 */
async function createCancellationAlertIfNeeded(
  shouldCalculateFees: boolean,
  cancellationFees: CancellationFees | null,
  user: User | null,
  event: Event
): Promise<void> {
  if (!shouldCalculateFees || !cancellationFees?.hasFees || !user) {
    return;
  }

  await createCancellationAlert({
    userId: user.id,
    bookingId: event.id,
    alertType: 'cancellation',
    eventType: event.type,
    bookingDate: event.date,
    businessDaysNotice: cancellationFees.businessDaysRemaining,
    feePercentage: cancellationFees.percentage,
    attendeeName: user.email || 'Client',
    attendeeEmail: user.email || '',
    plan: event.title,
    location: event.location,
    notes: `Annulation avec ${cancellationFees.businessDaysRemaining} jours ouvrés de préavis (moins de 14 jours ouvrés)`
  });
}

/**
 * Cancels the linked editorial booking when a shooting is cancelled
 */
async function cancelLinkedEditorialBooking(event: ShootingEvent): Promise<void> {
  if (!event.linkedEditorialBooking) {
    return;
  }

  const editorialBookingId = event.linkedEditorialBooking.id;

  try {
    const editorialCancelResult = await cancelBookingById(
      editorialBookingId,
      'Annulation automatique suite à l\'annulation du tournage'
    );

    if (editorialCancelResult.success) {
      logger.info('Linked editorial booking cancelled', { editorialBookingId });
    } else {
      logger.warn('Failed to cancel linked editorial booking', {
        error: editorialCancelResult.error,
        editorialBookingId
      });
    }
  } catch (error) {
    logger.error('Error cancelling linked editorial booking:', error);
  }
}

/**
 * Handles the result of the main booking cancellation. On success, runs onUpdate
 * (and waits for it if async) then closes so the list is refreshed before the modal closes.
 */
async function handleCancellationResult(
  result: { success: boolean; error?: string },
  onUpdate?: () => void | Promise<void>,
  onClose?: () => void
): Promise<void> {
  if (result.success) {
    toast.success('Rendez-vous annulé', {
      description: 'Votre rendez-vous a été annulé avec succès'
    });
    await Promise.resolve(onUpdate?.());
    onClose?.();
    return;
  }

  const isAlreadyCancelled = result.error?.includes('déjà été annulé');
  toast.error(isAlreadyCancelled ? 'Déjà annulé' : 'Erreur lors de l\'annulation', {
    description: result.error || 'Une erreur est survenue'
  });

  if (isAlreadyCancelled) {
    await Promise.resolve(onUpdate?.());
    onClose?.();
  }
}

interface BaseEvent {
  id: string;
  date: Date;
  title: string;
  time?: string;
  endTime?: string;
  location?: string;
  locationAddress?: string;
  status?: 'upcoming' | 'pending';
  bookingUid?: string;
  bookingCalendlyUri?: string;
  pbmName?: string;
  pbmAvatarUrl?: string;
}
interface ShootingEvent extends BaseEvent {
  type: 'shooting';
  directorName?: string;
  linkedEditorialBooking?: {
    id: string;
    date: Date;
    time?: string;
    pbmName?: string;
  };
}
interface PreparationEvent extends BaseEvent {
  type: 'preparation';
  relatedShootingDate?: Date;
}
interface EditorialEvent extends BaseEvent {
  type: 'editorial';
  relatedShootingDate?: Date;
}
type Event = ShootingEvent | PreparationEvent | EditorialEvent;

/**
 * Modal d'affichage des détails d'un événement (Tournage ou Préparation)
 * 
 * FONCTIONNALITÉS :
 * - Affichage des informations de l'événement
 * - Boutons d'action : Reprogrammer, Annuler
 * - Calcul automatique des frais d'annulation (tournages uniquement)
 * - Création d'alertes d'annulation si frais applicables
 * - Gestion du cas "déjà annulé" avec synchronisation automatique de l'UI
 * 
 * RÈGLES MÉTIER :
 * - Frais d'annulation = 50% si < 14 jours ouvrés avant tournage
 * - Préparations : pas de frais d'annulation
 * - Auto-refresh de la liste après annulation réussie
 * 
 * TYPES D'ÉVÉNEMENTS :
 * - shooting : Tournage avec location physique, frais d'annulation applicables
 * - preparation : Rendez-vous éditorial en visio, pas de frais
 * 
 * @param event - Données de l'événement à afficher
 * @param isOpen - État d'ouverture du modal
 * @param onClose - Callback de fermeture
 * @param onUpdate - Callback appelé après modification (annulation, reprogrammation)
 * 
 * @example
 * <EventDetailModal
 *   event={{
 *     id: "booking-123",
 *     type: "shooting",
 *     date: new Date("2025-01-15"),
 *     title: "Tournage - Présence",
 *     time: "14:00",
 *     location: "Paris 20 - Gambetta",
 *     bookingUid: "cal-uid-123"
 *   }}
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   onUpdate={() => refreshEvents()}
 * />
 */
interface EventDetailModalProps {
  event: Event | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
  isPastEvent?: boolean;
}

// Configuration par type d'événement
const EVENT_CONFIG: Record<EventType, {
  icon: LucideIcon;
  label: string;
  description: string;
  calculateFees: boolean;
  hasLocationId: boolean;
  renderLocation: (location: string) => React.ReactNode;
}> = {
  shooting: {
    icon: Camera,
    label: 'Tournage',
    description: 'Séance de tournage pour créer du contenu vidéo de qualité professionnelle. Notre équipe vous accompagne tout au long du processus pour des résultats optimaux.',
    calculateFees: true,
    hasLocationId: true,
    renderLocation: (location: string) => <div className="flex items-center gap-2 text-sm text-white/60">
        <MapPin className="h-4 w-4" />
        <span>{location}</span>
      </div>
  },
  preparation: {
    icon: Pen,
    label: 'Préparation',
    description: 'Réunion de préparation pour définir les sujets et organiser le tournage.',
    calculateFees: false,
    hasLocationId: false,
    renderLocation: (location: string) => null
  },
  editorial: {
    icon: Pen,
    label: 'RDV éditorial',
    description: 'Réunion éditoriale pour définir les sujets, organiser le tournage et aligner tous les détails créatifs et logistiques avec votre Personal Brand Manager.',
    calculateFees: false,
    hasLocationId: false,
    renderLocation: (location: string) => null
  }
};
export function EventDetailModal({
  event,
  isOpen,
  onClose,
  onUpdate,
  isPastEvent = false
}: Readonly<EventDetailModalProps>) {
  const [isCancelling, setIsCancelling] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showTopicsModal, setShowTopicsModal] = useState(false);

  // Zustand stores
  const user = useAuthStore((state) => state.user);
  const fetchBookingData = useBookingStore((state) => state.fetchBookingData);
  const getBookingData = useBookingStore((state) => state.getBookingData);
  const fetchDirector = useDirectorStore((state) => state.fetchDirector);
  const directors = useDirectorStore((state) => state.directors);

  // Get booking data from store
  const bookingDataRaw = event?.id ? getBookingData(event.id) : null;
  const bookingData = bookingDataRaw ? {
    productId: bookingDataRaw.product_id,
    userId: bookingDataRaw.user_id,
    locationId: bookingDataRaw.location_id || undefined
  } : null;

  // Get director data - prioritize event data (already fetched), fallback to store
  const directorId = bookingDataRaw?.director_id;
  const director = directorId ? directors[directorId] : null;
  const directorName = (event as any)?.directorName || (director ? `${director.first_name} ${director.last_name}` : null);
  const directorAvatar = (event as any)?.directorAvatarUrl || director?.avatar_url || null;

  // Précharger les avatars
  usePreloadAvatars({
    directorAvatar,
    pbmAvatar: event?.pbmAvatarUrl
  });
  const eventType = event?.type;
  const config = eventType ? EVENT_CONFIG[eventType] : null;

  // Charger les données du booking et le nom du directeur
  useEffect(() => {
    if (!event?.id) return;
    fetchBookingData(event.id, 'product_id, user_id, location_id, director_id, shooting_id');
  }, [event?.id, fetchBookingData]);

  // Charger le directeur si c'est un shooting
  useEffect(() => {
    if (event?.type === 'shooting' && bookingDataRaw?.director_id) {
      fetchDirector(bookingDataRaw.director_id);
    }
  }, [event?.type, bookingDataRaw?.director_id, fetchDirector]);

  // Calculer les frais d'annulation
  const cancellationFees = useMemo(() => {
    if (!event || !config) return null;
    return config.calculateFees ? calculateCancellationFees(event.date, event.type) : null;
  }, [event, config]);
  const confirmationMessage = useMemo(() => {
    if (!cancellationFees) return null;
    return getCancellationConfirmationMessage(cancellationFees);
  }, [cancellationFees]);

  const handleCancelConfirm = useCallback(async () => {
    if (!event?.id || !config) return;

    setShowCancelConfirm(false);
    setIsCancelling(true);

    try {
      // Create cancellation alert if fees apply (shooting only)
      await createCancellationAlertIfNeeded(config.calculateFees, cancellationFees, user, event);

      // Cancel linked editorial booking if this is a shooting
      if (event.type === 'shooting') {
        await cancelLinkedEditorialBooking(event);
      }

      const result = await cancelBookingById(event.id, 'Annulation demandée par le client');
      await handleCancellationResult(result, onUpdate, onClose);
    } catch (error) {
      logger.error('Error cancelling booking:', error);
      toast.error('Erreur lors de l\'annulation', {
        description: error instanceof Error ? error.message : 'Une erreur est survenue'
      });
    } finally {
      setIsCancelling(false);
    }
  }, [event, config, cancellationFees, user, onUpdate, onClose]);

  const handleRescheduleSuccess = useCallback(() => {
    onUpdate?.();
    setShowReschedule(false);
    onClose();
  }, [onUpdate, onClose]);

  const handleCancelClick = useCallback(() => {
    if (!event?.id) {
      toast.error("Impossible d'annuler", {
        description: 'Identifiant de réservation manquant'
      });
      return;
    }
    setShowCancelConfirm(true);
  }, [event?.id]);

  if (!event || !config) return null;
  const Icon = config.icon;
  const canCancel = !!(event.bookingUid || event.bookingCalendlyUri);

  // Titre spécifique selon le type d'événement
  const getTitle = () => {
    if (event.type === 'shooting' && (event.directorName || directorName)) {
      return `Votre tournage avec ${event.directorName || directorName}`;
    }
    if (event.type === 'editorial' && event.pbmName) {
      return `RDV éditorial avec ${event.pbmName}`;
    }
    if (event.type === 'preparation' && event.relatedShootingDate) {
      return 'Préparation pour votre prochain tournage';
    }
    return event.title;
  };
  return <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="bg-black/95 border border-white/10 text-white max-w-[calc(100%-2rem)] backdrop-blur-xl">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.3
      }}>
          {/* Header */}
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider mb-2 text-warning/90">
                  <Icon className="h-3 w-3" />
                  <span>{config.label}</span>
                </div>
                <h2 className="text-2xl text-white tracking-tight leading-tight">
                  {getTitle()}
                </h2>
              </div>
            </div>

            {/* Date, heure et lieu */}
            <div className="flex flex-col gap-2 text-sm text-white/60">
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-4 w-4" />
                <span>{event.date.toLocaleDateString('fr-FR', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}</span>
              </div>
              {event.time && <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>{event.endTime ? `${event.time} - ${event.endTime}` : event.time}</span>
                </div>}
              {event.location && config.renderLocation(event.type === 'shooting' && event.locationAddress ? event.locationAddress : event.location)}
            </div>
          </div>

          {/* Bouton rejoindre le viso pour les préparations et éditoriaux */}
          {(event.type === 'preparation' || event.type === 'editorial') && event.location && <div className="mt-6">
              <a href={event.location} target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-warning/20 hover:bg-warning/30 border border-warning/30 text-warning font-medium text-sm transition-all hover:scale-105">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Rejoindre la visioconférence
              </a>
            </div>}

          {/* Description */}
          <div className="mt-6 space-y-2">
            <p className="text-[10px] uppercase tracking-wider text-white/40">
              Description
            </p>
            <p className="text-sm text-white/70 leading-relaxed">
              {config.description}
            </p>
          </div>

          {/* Personal Brand Manager */}
          {event.pbmName && <div className="mt-6 space-y-2">
              <p className="text-[10px] uppercase tracking-wider text-white/40">
                Votre Personal Brand Manager
              </p>
              <div className="flex items-center gap-3">
                {event.pbmAvatarUrl && <img src={event.pbmAvatarUrl} alt={event.pbmName} className="w-10 h-10 rounded-full object-cover border border-white/10" />}
                <span className="text-sm text-white/90">{event.pbmName}</span>
              </div>
            </div>}

          {/* Director */}
          {event.type === 'shooting' && (event.directorName || directorName) && <div className="mt-6 space-y-2">
              <p className="text-[10px] uppercase tracking-wider text-white/40">
                Votre Réalisateur
              </p>
              <div className="flex items-center gap-3">
                {directorAvatar ? (
                  <img src={directorAvatar} alt={event.directorName || directorName || ''} className="w-10 h-10 rounded-full object-cover border border-white/10" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-white/10 border border-white/10 flex items-center justify-center">
                    <span className="text-white/60 text-sm font-medium">
                      {(event.directorName || directorName || '').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                    </span>
                  </div>
                )}
                <span className="text-sm text-white/90">{event.directorName || directorName}</span>
              </div>
            </div>}

          {/* Bouton sujets pour tournage */}
          {event.type === 'shooting' && bookingDataRaw?.shooting_id && (
            <div className="mt-6">
              <button
                onClick={() => setShowTopicsModal(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 border border-amber-400/20 text-amber-400 font-medium text-sm transition-all"
              >
                <Lightbulb className="h-4 w-4" />
                Voir les sujets prévus
              </button>
            </div>
          )}

          {/* Actions */}
          {!isPastEvent && (
            <div className="mt-8 flex gap-3">
              <button onClick={() => setShowReschedule(true)} disabled={!canCancel} className="flex-1 glass rounded-xl py-3 px-4 text-sm text-warning hover:text-warning/80 hover:bg-warning/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none">
                Modifier la date
              </button>
              <button onClick={handleCancelClick} disabled={isCancelling || !canCancel} className="flex-1 glass rounded-xl py-3 px-4 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all border border-red-400/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 focus:outline-none">
                {isCancelling ? <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Annulation...</span>
                  </> : 'Annuler le RDV'}
              </button>
            </div>
          )}
        </motion.div>
      </DialogContent>

      {/* Modal de reprogrammation */}
      {(event.bookingUid || event.bookingCalendlyUri) && bookingData && event.id && (
        <RescheduleModal 
          bookingId={event.id} 
          currentDate={event.date} 
          eventTitle={event.title} 
          productId={bookingData.productId} 
          userId={bookingData.userId} 
          locationId={bookingData.locationId} 
          eventType={event.type} 
          relatedShootingDate={(event.type === 'preparation' || event.type === 'editorial') ? event.relatedShootingDate : undefined} 
          linkedEditorialBookingDate={event.type === 'shooting' && 'linkedEditorialBooking' in event ? event.linkedEditorialBooking?.date : undefined} 
          isOpen={showReschedule} 
          onClose={() => setShowReschedule(false)} 
          onSuccess={handleRescheduleSuccess} 
        />
      )}

      {/* Confirmation d'annulation */}
      <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <AlertDialogContent className="bg-black/95 border border-white/10 text-white backdrop-blur-xl max-w-[calc(100%-2rem)] mx-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              {event.type === 'preparation' ? 'Annuler ce rendez-vous éditorial ?' : confirmationMessage?.title || 'Annuler ce tournage ?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60 whitespace-pre-line">
              {event.type === 'preparation' ? 'Êtes-vous sûr de vouloir annuler ce rendez-vous éditorial ? Cette action est irréversible.' : confirmationMessage?.description || 'Êtes-vous sûr de vouloir annuler ce tournage ? Cette action est irréversible.'}
            </AlertDialogDescription>
            
            {/* Frais d'annulation pour shooting */}
            {config.calculateFees && cancellationFees?.hasFees && <div className="mt-4 p-3 bg-red-500/10 border border-red-400/20 rounded-lg">
                <p className="text-sm text-red-400">
                  <strong>Frais applicables :</strong> 50% du forfait
                </p>
                <p className="text-xs text-red-400/80 mt-1">
                  Il reste {cancellationFees.businessDaysRemaining} jours ouvrés avant le tournage
                </p>
              </div>}
            
            {config.calculateFees && !cancellationFees?.hasFees && cancellationFees && <div className="mt-4 p-3 bg-green-500/10 border border-green-400/20 rounded-lg">
                <p className="text-sm text-green-400">
                  ✓ Aucun frais d'annulation
                </p>
                <p className="text-xs text-green-400/80 mt-1">
                  Plus de 14 jours ouvrés avant le tournage ({cancellationFees.businessDaysRemaining} jours ouvrés)
                </p>
              </div>}

            {/* Info pour preparation (pas de frais) */}
            {!config.calculateFees && <div className="mt-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
                <p className="text-sm text-warning">
                  ℹ️ Aucun frais d'annulation pour les rendez-vous éditoriaux
                </p>
              </div>}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="glass border-white/10 text-white hover:bg-white/5">
              Retour
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleCancelConfirm} className="bg-red-500/20 text-red-400 border border-red-400/20 hover:bg-red-500/30">
              Confirmer l'annulation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modale des sujets pour le tournage */}
      {event.type === 'shooting' && bookingDataRaw?.shooting_id && (
        <ShootingTopicsModal
          shootingId={bookingDataRaw.shooting_id}
          isOpen={showTopicsModal}
          onClose={() => setShowTopicsModal(false)}
        />
      )}
    </Dialog>;
}